import { ChevronDown, ChevronUp } from 'react-feather';
import { MouseEvent, useCallback, useRef, useState } from 'react';
import { useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { Locale } from '__generated__/graphql';

import { EditorProps } from '../../common/Tiptap/helpers';
import { Errors, toggleInList } from '../../common/helpers';
import { MenuBarButton } from '../../common/Tiptap/Layout';
import { Translation } from './helpers';
import Editor from '../../common/Tiptap';
import GetTranslationQuery from './GetTranslationQuery';
import UI from '../../common/UI';
import useLazyQuery from '../../api/useLazyQuery';
import useLocale from '../../common/useLocale';
import useProject from '../useProject';

export interface EditTranslationsModalProps {
  defaultLocale: Locale;
  defaultValue: string | null;
  translations: Translation[];
  errors?: Errors;
  title?: string;
  inputType?: InputType;
  maxLength?: number;
  onSave?: (defaultValue: string | null, translations: Translation[]) => void;
  editorProps?: Partial<EditorProps>;
}

type InputType = 'string' | 'text' | 'textarea';

export const EditTranslationsModal = ({
  defaultLocale, defaultValue, translations, errors, title, inputType = 'string', maxLength, onSave, editorProps,
}: EditTranslationsModalProps) => {
  const { t } = useTranslation();
  const { sortedLocales } = useLocale();
  const fieldErrors = errors || {};
  const project = useProject();

  const textAreaRef = useRef<{ [locale: string]: HTMLTextAreaElement | null; }>({});

  // 1) Remove the translation for the default locale if its present in the list of translations.
  // 2) Add the given default locale and value to the list of translations.
  // 3) Add the translations to the form, keyed by locale.
  const form = useForm({
    values: [
      ...translations.filter((translation) => translation.locale !== defaultLocale),
      {
        locale: defaultLocale,
        value: defaultValue,
      },
    ].reduce((translations, translation) => ({
      ...translations,
      [translation.locale]: translation.value,
    }), {}) as { [key in Locale]: string | null },
  });

  const [visibleLocales, setVisibleLocales] = useState([
    defaultLocale,
    ...translations
      .filter((translation) => translation.value && translation.locale !== defaultLocale)
      .map((translation) => translation.locale),
  ]);

  const toggleLocale = (clickEvent: MouseEvent<HTMLLabelElement>, locale: Locale) => {
    if (visibleLocales.includes(locale)) {
      clickEvent.preventDefault();
    }

    setVisibleLocales(toggleInList(visibleLocales, locale));
  };

  const showLocale = (locale: Locale) => {
    if (!visibleLocales.includes(locale)) {
      setVisibleLocales(([...visibleLocales, locale]));
    }
  };

  const save = () => {
    onSave(form.values[defaultLocale], Object.keys(form.values).map((locale: Locale) => ({
      locale,
      value: form.values[locale] || null, // Unset the translation when empty.
    })));
  };

  // Default locale first, followed by the other locales sorted by language name.
  const locales: Locale[] = [
    defaultLocale,
    ...sortedLocales.filter((locale) => locale !== defaultLocale),
  ];

  const sourceValue = form.values[defaultLocale];

  const [translating, setTranslating] = useState<Record<Locale, boolean>>(
    () => Object.fromEntries(locales.map((locale) => [locale, false])) as Record<Locale, boolean>,
  );

  const [translate] = useLazyQuery(GetTranslationQuery);
  const editorRef = useRef(0);

  const handleTranslation = useCallback((locale: Locale) => {
    setTranslating((values) => ({ ...values, [locale]: true }));

    translate({
      variables: {
        project_id: project.id,
        source: defaultLocale,
        target: locale,
        content: sourceValue,
        has_html: inputType === 'text',
      },
    })
      .then((result) => {
        if (!result.data) {
          return;
        }

        const { data: { project: { translation } } } = result;

        editorRef.current++;

        form.set((values) => ({
          ...values,
          [locale]: translation,
        }));
      })
      .finally(() => setTranslating((values) => ({ ...values, [locale]: false })));
  }, [project.id, defaultLocale, sourceValue, translate]);

  return (
    <UI.Form onSubmit={save}>
      <UI.FormGrid>
        <UI.H4>
          {title || t('translations')}
        </UI.H4>

        <UI.HR />

        {locales.map((locale) => (
          <UI.InputGroup
            sc={{ mb: 0, invalid: !!fieldErrors[locale] }}
            key={locale}
          >
            <UI.InputLabel
              htmlFor={`Translation_${locale}`}
              id={`TranslationLabel_${locale}`}
              onClick={(event) => toggleLocale(event, locale)}
              sc={{ mb: 0 }}
              style={{ cursor: 'pointer' }}
            >
              {locale === defaultLocale && (
                <>
                  {t('default')}
                  {` – ${t(`common:locales.${locale}`)}`}
                </>
              )}
              {locale !== defaultLocale && t(`common:locales.${locale}`)}
              {' '}
              <UI.Icon>
                {visibleLocales.includes(locale) ? <ChevronUp /> : <ChevronDown />}
              </UI.Icon>
            </UI.InputLabel>
            <UI.Div
              sc={{ pt: visibleLocales.includes(locale) ? 1 : 0, mt: 0 }}
              style={!visibleLocales.includes(locale) ? {
                height: 0,
                overflow: 'hidden',
              } : undefined}
            >
              {inputType === 'string' && (
                <UI.InputWrapper>
                  <UI.Input
                    id={`Translation_${locale}`}
                    maxLength={maxLength}
                    {...form.text(locale, { onFocus: () => showLocale(locale) })}
                    placeholder={form.get(defaultLocale)}
                  />
                  {locale !== defaultLocale && (
                    <UI.Button
                      sc={{ blank: true, small: true }}
                      disabled={!sourceValue || translating[locale]}
                      onClick={() => handleTranslation(locale)}
                      aria-label={t('translate_option', { option: t(`common:locales.${locale}`) })}
                    >
                      <UI.Icon>
                        {translating[locale] ? <UI.Loader sc={{ brand: 'gray.800', size: 12 }} /> : <UI.Icons.Ai />}
                      </UI.Icon>
                      {' '}
                      {t('translate')}
                    </UI.Button>
                  )}
                </UI.InputWrapper>
              )}
              {inputType === 'textarea' && (
                <div>
                  {locale !== defaultLocale && (
                    <UI.FlexContainer
                      sc={{ justifyContent: 'end', relative: true }}
                      style={{ padding: 10, marginBottom: -51, borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}
                      onClick={() => textAreaRef.current?.[locale]?.focus()}
                    >
                      <MenuBarButton
                        disabled={!sourceValue || translating[locale]}
                        onClick={() => handleTranslation(locale)}
                        aria-label={t('translate_option', { option: t(`common:locales.${locale}`) })}
                      >
                        <UI.Icon>
                          {translating[locale] ? <UI.Loader sc={{ brand: 'gray.800', size: 12 }} /> : <UI.Icons.Ai />}
                        </UI.Icon>
                        {' '}
                        {t('translate')}
                      </MenuBarButton>
                    </UI.FlexContainer>
                  )}
                  <UI.TextArea
                    id={`Translation_${locale}`}
                    {...form.textarea(locale, { onFocus: () => showLocale(locale) })}
                    placeholder={form.get(defaultLocale)}
                    style={{ paddingTop: locale !== defaultLocale ? 62 : undefined }}
                    ref={(element) => { textAreaRef.current[locale] = element; }}
                  />
                </div>
              )}
              {inputType === 'text' && (
                <Editor
                  value={form.values[locale]}
                  onChange={(value) => form.set(locale, value)}
                  stickyMenuBar={false}
                  {...editorProps}
                  attributes={{
                    ...editorProps?.attributes,
                    id: `Translation_${locale}`,
                    'aria-labelledby': `TranslationLabel_${locale}`,
                  }}
                  onFocus={() => showLocale(locale)}
                  translateButton={locale !== defaultLocale ? () => (
                    <MenuBarButton
                      disabled={!sourceValue || translating[locale]}
                      onClick={() => handleTranslation(locale)}
                      aria-label={t('translate_option', { option: t(`common:locales.${locale}`) })}
                    >
                      <UI.Icon>
                        {translating[locale] ? <UI.Loader sc={{ brand: 'gray.800', size: 12 }} /> : <UI.Icons.Ai />}
                      </UI.Icon>
                      {' '}
                      {t('translate')}
                    </MenuBarButton>
                  ) : undefined}
                  key={editorRef.current}
                />
              )}
            </UI.Div>
            <UI.ErrorMessages attribute={title || t('value')} errors={fieldErrors[locale]} />
          </UI.InputGroup>
        ))}

        <UI.HR />

        <UI.Div sc={{ textAlign: 'right' }}>
          <UI.Button
            type="submit"
            sc={{ brand: 'secondary' }}
          >
            {t('continue')}
          </UI.Button>
        </UI.Div>
      </UI.FormGrid>
    </UI.Form>
  );
};

interface UseEditTranslationsModalProps {
  defaultLocale: Locale;
  defaultValue: string | null;
  translations: Translation[];
  errors?: Errors;
  title?: string;
  inputType?: InputType;
  maxLength?: number;
  onSave: (defaultValue: string | null, translations: Translation[]) => void;
  editorProps?: Partial<EditorProps>;
}

const useEditTranslationsModal = ({
  defaultLocale, defaultValue, translations, errors, title, inputType, maxLength, onSave, editorProps,
}: UseEditTranslationsModalProps) => {
  const [openModal, closeModal] = useModal(() => {
    const save = (defaultValue: string | null, translations: Translation[]) => {
      onSave(defaultValue, translations);
      closeModal();
    };

    return (
      <UI.Modal maxWidth={inputType === 'text' ? 800 : 600} close={closeModal}>
        <EditTranslationsModal
          defaultLocale={defaultLocale}
          defaultValue={defaultValue}
          translations={translations}
          errors={errors}
          title={title}
          inputType={inputType}
          maxLength={maxLength}
          onSave={save}
          editorProps={editorProps}
        />
      </UI.Modal>
    );
  }, [defaultLocale, defaultValue, translations, title, inputType, maxLength, editorProps, onSave]);

  return [openModal, closeModal];
};

export default useEditTranslationsModal;
